import type { ApiFailure } from '@/core/either';
import { ErrorsCommon } from '../errors/common.error';

interface ErrorMessageOptions {
  failure: ApiFailure;
  errorMap?: Record<string, string>;
  context?: string;
  defaultMessage?: string;
}

/**
 * Resuelve un mensaje de error para mostrar al usuario.
 * Combina ErrorsCommon con el mapa específico (errorMap pisa a común).
 *
 * La búsqueda usa `Object.hasOwn` y no el operador `in` porque `in` recorre la
 * cadena de prototipos: un `reason` llamado como un miembro de `Object.prototype`
 * ('toString', 'constructor') devolvía esa función en lugar del mensaje por
 * defecto, rompiendo el contrato de retorno `string`.
 *
 * @param failure ApiFailure con el reason del error.
 * @param errorMap Mapa de errores específico del dominio (opcional).
 * @param context Contexto para buscar reason_CONTEXT (opcional).
 * @param defaultMessage Mensaje personalizado si no se encuentra el reason (opcional).
 * @returns Mensaje de error localizado.
 */
export function getReasonMessage({
  failure,
  errorMap,
  context,
  defaultMessage = 'Ha ocurrido un error. Vuelve a intentar o contacta a soporte.',
}: ErrorMessageOptions): string {
  const { reason } = failure;

  const combinedErrorMap = {
    ...ErrorsCommon,
    ...(errorMap ?? {}),
  };
  if (context) {
    const contextualKey = `${reason}_${context.toUpperCase()}`;
    if (Object.hasOwn(combinedErrorMap, contextualKey)) {
      return combinedErrorMap[contextualKey];
    }
  }

  if (Object.hasOwn(combinedErrorMap, reason)) {
    return combinedErrorMap[reason];
  }

  return defaultMessage;
}
